import { Fragment, useState, useEffect, useCallback, useContext } from 'react';
import dayjs from 'dayjs';
import Toolbar, { Item } from 'devextreme-react/toolbar';
import List from 'devextreme-react/list';
import { LoadPanel } from 'devextreme-react/load-panel';
import Button from 'devextreme-react/button';
import DropDownBox from "devextreme-react/drop-down-box";
import { Template } from 'devextreme-react/core/template';
import { UserPanel } from '~/components/user-panel/user-panel';
import { WeekNavigator } from "~/components/week-navigator/week-navigator";
import { CallLogPopup, closeAllCallLogPopups } from '~/components/call-log-popup/call-log-popup';
import { NameIdentifier } from '~/helpers/miscellaneous'
import { CreateAuthorizedStore } from "~/helpers/data";
import { AppContext } from '~/app-context';
import { permissions } from '~/app-const';
import './header.scss';

function getDatesOfCurrentWeek() {
  const now = new Date();
  const day = now.getDay();

  const start = new Date(now);
  start.setDate(now.getDate() - day);

  const end = new Date(start);
  end.setDate(start.getDate() + 6);

  return { startDate: start, endDate: end };
}

function RenderItem(item) {
  const date = dayjs(item.CallDate);

  return (
    <Fragment>
      <div>
        <i className={(item.Type === 'Member') ? 'dx-icon-user' : 'dx-icon-card'}></i>
        <span className="calls-item-name">{NameIdentifier(item.PreferredFirstName, item.FirstName, item.LastName)}</span>
      </div>
      <div>
        Created by {item.User}
      </div>
      <div className="callDate">
        {date.format('YYYY-MM-DD HH:mm a')}
      </div>
    </Fragment>
  )
}

export const Header = ({ menuToggleEnabled, title, toggleMenu, userMenuItems }) => {
  const URL = `${window.env.apiEndpoint}/api/v1/CallLogs`;
  const { hasPermission } = useContext(AppContext);

  const [calls, setCalls] = useState([]);
  const [dateRange, setDateRange] = useState(getDatesOfCurrentWeek());
  const [callLogPopups, setCallLogPopups] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const userHasCallPermission = hasPermission(permissions.callLogRead)
    || hasPermission(permissions.callLogWrite)
    || hasPermission(permissions.callLogDelete);

  const loadCalls = useCallback(async (range) => {
    setIsLoading(true);
    try {
      const ds = CreateAuthorizedStore(
        { loadUrl: `${URL}/GetOpenCalls` },
        {
          load: {
            timeZone: window.env.timeZone,
            start: dayjs(range.startDate).format("YYYY-MM-DD"),
            end: dayjs(range.endDate).format("YYYY-MM-DD"),
          }
        },
        "Id"
      );
      const result = await ds.load();
      setCalls(result);
    } finally {
      setIsLoading(false);
    }
  }, [URL]);

  useEffect(() => {
    loadCalls(dateRange);
  }, [dateRange, loadCalls]);

  const setWeekDates = useCallback((range) => {
    setDateRange(range);
  }, []);

  const handleNewCall = useCallback(() => {
    setCallLogPopups((prev) => [...prev, { key: Date.now() }]);
  }, []);

  const handleOpened = useCallback(() => {
    setDateRange(getDatesOfCurrentWeek());
  }, []);

  return (
    <header className={'header-component'}>
      <Toolbar className={'header-toolbar'}>
        <Item
          visible={menuToggleEnabled}
          location={'before'}
          widget={'dxButton'}
          cssClass={'menu-button'}
        >
          <Button icon="menu" stylingMode="text" onClick={toggleMenu} />
        </Item>
        <Item
          location={'before'}
          cssClass={'header-title'}
          text={title}
          visible={!!title}
        />
        <Item
          visible={userHasCallPermission}
          location={'after'}
          locateInMenu={'auto'}>
          <div className="open-calls-wrapper">
            <Button
              hint="Create new call..."
              icon="tel"
              stylingMode="text"
              onClick={handleNewCall} />
            <Button
              hint="Close all open call windows..."
              icon="close"
              stylingMode="text"
              onClick={closeAllCallLogPopups} />
          <DropDownBox
            dropDownOptions={{
              width: "auto",
              maxWidth: 600,
            }}
            width={250}
            placeholder="Open Calls"
            onOpened={handleOpened}
            contentRender={() => (
              <div id="callsDropdownContent" className="calls-dropdown-content">
                <LoadPanel
                  visible={isLoading}
                  container="#callsDropdownContent"
                  position={{ of: '#callsDropdownContent' }}
                  shading={false}
                />
                <div className="calls-list-wrapper">
                  <List
                    dataSource={calls}
                    keyExpr="Id"
                    height="100%"
                    itemRender={RenderItem}
                    onItemClick={(e) => {
                      const id = e.itemData.Id;
                      setTimeout(() => setCallLogPopups((prev) => [...prev, { key: Date.now(), Id: id }]), 0);
                    }}
                  />
                </div>
                <div className="calls-total">
                  Total Open Calls: {calls.length}
                </div>
                <div className="calls-footer">
                  <WeekNavigator onWeekChange={setWeekDates} dateRange={dateRange} />
                </div>
              </div>
            )}
          />
          </div>
        </Item>
        <Item
          location={'after'}
          locateInMenu={'auto'}
          menuItemTemplate={'userPanelTemplate'}
        >
          <Button
            className={'user-button authorization'}
            width={170}
            height={'100%'}
            stylingMode={'text'}
          >
            <UserPanel menuItems={userMenuItems} menuMode={'context'} />
          </Button>
        </Item>
        <Template name={'userPanelTemplate'}>
          <UserPanel menuItems={userMenuItems} menuMode={'list'} />
        </Template>
      </Toolbar>
      {callLogPopups.map((popup, index) => (
        <CallLogPopup
          key={popup.key}
          visible
          Id={popup.Id}
          hide={() => setCallLogPopups((prev) => prev.filter((p) => p.key !== popup.key))}
          reloadCalls={() => loadCalls(dateRange)}
          gridEditing={false}
          offset={`${50 * index} ${50 * index}`}
          refreshCallLogDropDownButton={() => {}}
        />
      ))}
    </header>
  );
}